import React, { useState, useEffect, useMemo } from 'react';
import { categoryService } from '../../../../services/apiService';
import { FaSpinner } from 'react-icons/fa';

const ADD_CATEGORY_VALUE = '__add_category__';

function parentIdOf(cat) {
  if (!cat?.parentCategory) return null;
  if (typeof cat.parentCategory === 'object' && cat.parentCategory !== null) {
    return (cat.parentCategory._id || cat.parentCategory.id)?.toString() || null;
  }
  return String(cat.parentCategory);
}

/**
 * Category-only selector. Stores selected name in `category`.
 */
const HierarchicalCategorySelector = ({
  category: parentName = '',
  onChange,
  required = false,
}) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customCategory, setCustomCategory] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await categoryService.getAllActiveCategoriesFlat({ forVendor: true });
        let list = [];
        if (result?.categories && Array.isArray(result.categories)) list = result.categories;
        else if (Array.isArray(result)) list = result;
        else if (result?.data?.categories) list = result.data.categories;
        if (!cancelled) setCategories(list);
      } catch (e) {
        console.error('Failed to load categories:', e);
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const parents = useMemo(
    () => categories.filter((c) => !parentIdOf(c)),
    [categories]
  );

  const parentNames = useMemo(
    () => new Set(parents.map((p) => p.name)),
    [parents]
  );

  const isCustomSelected = !!parentName && !parentNames.has(parentName);

  useEffect(() => {
    if (isCustomSelected) {
      setIsAddingCustom(true);
      setCustomCategory(parentName);
      return;
    }
    if (!isAddingCustom) {
      setCustomCategory('');
    }
  }, [isCustomSelected, parentName, isAddingCustom]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <FaSpinner className="animate-spin" />
        <span>Loading categories...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category (parent) {required ? <span className="text-red-500">*</span> : null}
        </label>
        <select
          value={isCustomSelected ? ADD_CATEGORY_VALUE : parentName}
          onChange={(e) => {
            const v = e.target.value;
            if (v === ADD_CATEGORY_VALUE) {
              setIsAddingCustom(true);
              setCustomCategory('');
              onChange({ category: '', subCategory: '' });
              return;
            }
            setIsAddingCustom(false);
            setCustomCategory('');
            onChange({ category: v, subCategory: '' });
          }}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
          required={required && !isAddingCustom}
        >
          <option value="">{required ? 'Select category' : '—'}</option>
          {parents.map((cat) => (
            <option key={cat._id || cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
          <option value={ADD_CATEGORY_VALUE}>+ Add category</option>
        </select>
      </div>

      {isAddingCustom && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add category name {required ? <span className="text-red-500">*</span> : null}
          </label>
          <input
            type="text"
            value={customCategory}
            onChange={(e) => {
              const value = e.target.value;
              setCustomCategory(value);
              onChange({ category: value, subCategory: '' });
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Type new category"
            required={required}
          />
        </div>
      )}

    </div>
  );
};

export default HierarchicalCategorySelector;
